import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRange } from '../../context/RangeContext/RangeContext';
import { useGame } from '../../context/GameContext';
import { ButtonLarge } from '../../components/common/buttons/ButtonLarge/ButtonLarge';
import RangeMatrix from '../../components/RangeMatrix/RangeMatrix';

import './range-screen.css';
import '../../components/RangeMatrix/range-matrix.css'

export const RangeScreen = () => {
  const { startNewGame } = useGame();
  const { activeRange, loadRange, isLoading: rangeLoading } = useRange();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasLoaded = useRef(false);
  
  const config = location.state?.config;

  // Directly provide the range to the RangeMatrix component
  // No additional processing to avoid potential issues
  useEffect(() => {
    if (!config) {
      console.error('No config provided');
      setError('Configuration missing');
      setIsLoading(false);
      return;
    }

    if (hasLoaded.current) return;

    const loadRangeData = async () => {
      try {
        console.log('Loading range with config:', config);
        
        // Validate config
        if (!config.range) {
          throw new Error('Range path is required');
        }

        // Load the range
        await loadRange({
          position: config.heroPosition || config.position,
          betType: config.betType,
          rangePath: config.range
        });

        hasLoaded.current = true;
        
        // Add this to log the actual range data for debugging
        console.log('Loaded range data:', {
          type: activeRange ? (activeRange instanceof Map ? 'Map' : typeof activeRange) : 'null/undefined',
          size: activeRange instanceof Map ? activeRange.size : 
                (activeRange && typeof activeRange === 'object' ? Object.keys(activeRange).length : 0)
        });
      } catch (error) {
        console.error('Error in loadRangeData:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadRangeData();
  }, [config, loadRange, activeRange]);

  // Format title based on configuration
  // Format title based on configuration
// Format title based on configuration
const formatTitle = (position, betType, config = {}) => {
  if (!position || !betType) {
    return 'Range';
  }

  // Normalize betType to uppercase for consistent comparison
  const normalizedBetType = betType.toUpperCase();
  
  console.log('Formatting title with:', { position, normalizedBetType, config });

  // Handle SB Limp scenarios
  if (normalizedBetType === 'SB_LIMP' || normalizedBetType === 'LIMP') {
    return `${position} Limp`;
  }

  // If BB is involved as the opener, this is invalid
  if (config.openPosition === 'BB' && !config.threeBetPosition) {
    console.error('Invalid config: BB cannot be opener');
    return 'Invalid Range Configuration';
  }

  // First check for FACING_3BET_CALL specifically - it needs special handling
  if (normalizedBetType === 'FACING_3BET_CALL') {
    return `${position} Call vs ${config.threeBetPosition} 3Bet`;
  }

  // Handle 4bet cases
  if (normalizedBetType === 'FACING_3BET_4BET') {
    const opponent = config.threeBetPosition || config.openPosition;
    return opponent ? `${position} 4Bet vs ${opponent} 3Bet` : `${position} 4Bet`;
  }

  // Handle 3bet cases
  if (normalizedBetType === 'FACING_OPEN_3BET') {
    return config.openPosition
      ? `${position} 3Bet vs ${config.openPosition} Open`
      : `${position} 3Bet`;
  }

  // Handle other call cases
  if (normalizedBetType === 'FACING_OPEN_CALL') {
    return `${position} Call vs ${config.openPosition} Open`;
  }

  // Handle open cases
  if (normalizedBetType === 'OPEN' || config.originalBetType === 'Open') {
    if (position === 'BB') {
      console.error('Invalid config: BB cannot open');
      return 'Invalid Range Configuration';
    }
    return `${position} Open`;
  }

  // Default case with more diagnostics
  console.log('Using default title case for:', normalizedBetType);
  return `${position} ${betType}`;
}

const handleBeginQuiz = () => {
  if (!config) {
    console.error('No config available for quiz');
    return;
  }
  
  console.log('Starting game with config:', config);
  
  // Validate the config before starting the game
  const requiredFields = ['position', 'betType', 'range'];
  const missingFields = requiredFields.filter(field => !config[field]);
  
  if (missingFields.length > 0) {
    console.error(`Missing required config fields: ${missingFields.join(', ')}`);
    setError(`Configuration is missing required fields: ${missingFields.join(', ')}`);
    return;
  }
  
  // Ensure threeBetPosition is correctly set for 3bet call scenarios
  if (config.betType === 'FACING_3BET_CALL' && !config.threeBetPosition) {
    console.error('Missing threeBetPosition for 3bet call scenario');
    setError('Invalid configuration: Missing 3bettor position');
    return;
  }
  
  // Ensure the range data exists
  if (!activeRange || (activeRange instanceof Map && activeRange.size === 0)) {
    console.error('No active range data available');
    setError('Range data is not loaded properly. Please try again.');
    return;
  }
  
  // Clone the config to ensure we don't have reference issues
  const gameConfig = {
    ...config,
    // Ensure these fields are set correctly for the game
    position: config.position || config.heroPosition,
    totalHands: config.totalHands || 10,
    // Add any other necessary fields
  };
  
  try {
    console.log('Starting new game with verified config:', gameConfig);
    startNewGame(gameConfig);
    
    // Add a small delay to ensure context is updated before navigation
    setTimeout(() => {
      console.log('Navigating to preflop screen');
      navigate('/preflop');
    }, 100);
  } catch (error) {
    console.error('Error starting game:', error);
    setError(`Failed to start quiz: ${error.message || 'Unknown error'}`);
  }
};

  // Actual loading state combines internal + external loading states
  const loading = isLoading || rangeLoading;

  if (error) {
    return (
      <div className="range-config">
        <div className="range-screen-content">
          <div className="range-screen-navigation">
            <button className="range-nav-link" onClick={() => navigate('/')}>Dashboard</button>
            <button className="range-nav-link" onClick={() => navigate('/quiz-config')}>Reset</button>
          </div>
          <div className="error-message" style={{color: 'red', padding: '20px'}}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="range-config">
      <div className="range-screen-content">
        <div className="range-screen-navigation">
          <button className="range-nav-link" onClick={() => navigate('/')}>Dashboard</button>
          <button className="range-nav-link" onClick={() => navigate('/quiz-config')}>Reset</button>
        </div>

        <h1 className="range-screen-title">6max Cash Game</h1>
        <div className="range-info">
          <h2 className="range-subtitle">
          {formatTitle(
            config?.position || config?.heroPosition,
            config?.betType,
            {
              openPosition: config?.openPosition,
              threeBetPosition: config?.threeBetPosition,
              mappedBetType: config?.mappedBetType,
              callType: config?.callType
            }
          )}
          </h2>
          <p className="range-description">
            This is the range you'll be using. When you're ready, press Begin Quiz.
          </p>
        </div>

        {loading ? (
          <div className="loading-container" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            width: '100%',
            fontSize: '16px',
            color: '#3E3E45'
          }}>
            Loading range...
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '0px' }}>
              {/* Pass the activeRange directly without processing */}
              <RangeMatrix activeRange={activeRange} />
            </div>
            
            <div className="range-button-container">
              <ButtonLarge 
                buttonLargeClassName="button-large begin-quiz-button"
                onClick={handleBeginQuiz}
                text="Begin Quiz"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};